// MUI-free copy of Ui-components/helper-section.
import {Fragment, memo} from 'react';
import type {CSSProperties, ReactNode} from 'react';
import Link from 'next/link';
import type {StaticImageData} from 'next/image';
import parse from 'html-react-parser';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import type {OptimizedImageProps} from '@/components/atoms/OptimizedImage';
import FadeInUp from '@/components/atoms/FadeInUp';
import styles from './HelperSection.module.scss';

export interface HelperSectionItem {
    icon: StaticImageData | string;
    /** '' renders the card without a link (adds `non-link-box`). */
    redirect: string;
    heading?: string;
    body?: string;
}

interface HelperSectionProps {
    data?: HelperSectionItem[];
    className?: string;
    /**
     * Was an Emotion `sx` on the icon box. Plain version: inline style only — nested selectors such as
     * devops' `'& img'` cannot be expressed here, pass `iconBoxClassName` for those.
     */
    iconBoxSx?: CSSProperties;
    iconBoxClassName?: string;
}

const ITEM_CLASS = `review-card-parent ${styles.item}`;
const FADE_CLASS = 'height-full';

const Heading = ({html}: {html?: string}) => (
    <h3 className={`recipe-card-heading helper-section-header-link ${styles.heading}`}>{html ? parse(html) : ''}</h3>
);

const renderCard = (
    subData: HelperSectionItem,
    className: string | undefined,
    classn: string,
    iconBoxSx?: CSSProperties,
    iconBoxClassName?: string
) => (
    // `MuiCard-root` kept: globals.scss `.renowed-client-wrapper .MuiCard-root` (≤1199px) still targets it.
    <div className={`MuiCard-root ${styles.card} recipe-review-card ${classn} ${className}`}>
        <div className={styles.cardHeader}>
            <div className={styles.cardHeaderAvatar}>
                <div className={iconBoxClassName ? `helper-card-icon-box ${iconBoxClassName}` : 'helper-card-icon-box'} style={iconBoxSx}>
                    <OptimizedImage
                        {...({src: subData.icon, alt: subData.heading ?? ''} as OptimizedImageProps)}
                        className="helper-card-icon helper-card-icon-sizing"
                        priority={false}
                    />
                </div>
            </div>
            {/* MUI CardHeader always rendered its (here empty) content slot. */}
            <div className={styles.cardHeaderContent} />
        </div>
        {subData.redirect !== '' ? (
            <Link href={subData?.redirect} target="_blank" className="services_link menu-links">
                <Heading html={subData.heading} />
            </Link>
        ) : (
            <Heading html={subData.heading} />
        )}

        <div className={`helper-card-content-wrapper ${styles.cardContent}`}>
            <p className={`card-content data_links helper-section-card-content ${styles.body}`}>
                {subData.body ? parse(subData.body) : ''}
            </p>
        </div>
    </div>
);

/** Renders grid items only — the caller owns the `spacing={2}` container (see HelperSectionGrid). */
const HelpSection = ({data, className, iconBoxSx, iconBoxClassName}: HelperSectionProps) => (
    <>
        {data?.map((subData, index) => (
            <Fragment key={index + 1}>
                <div className={ITEM_CLASS}>
                    <FadeInUp className={FADE_CLASS}>
                        {subData.redirect !== ''
                            ? renderCard(subData, className, '', iconBoxSx, iconBoxClassName)
                            : renderCard(subData, className, 'non-link-box', iconBoxSx, iconBoxClassName)}
                    </FadeInUp>
                </div>
            </Fragment>
        ))}
    </>
);

interface HelperSectionGridProps {
    children: ReactNode;
    className?: string;
}

/** Plain stand-in for the page-owned `<Grid container spacing={2}>` that wraps HelperSection. */
export const HelperSectionGrid = memo(({children, className}: HelperSectionGridProps) => (
    <div className={className ? `${styles.grid} ${className}` : styles.grid}>{children}</div>
));
HelperSectionGrid.displayName = 'HelperSectionGrid';

export default memo(HelpSection);
